/**
 * web/src/api/purge.ts
 * Admin client for POST /api/admin/purge-workflows
 * (server: src/pixel/api/admin/purge.py).
 *
 * Wipes every workflow + its approvals so a tester can re-trigger the same
 * HubSpot deals and monday speakers from a clean slate — cancelling a
 * workflow doesn't free its triggerSource for a re-run, only deleting does.
 * Server-side this is admin-only AND refused on production.
 */
import { api } from '../lib/api';

/** Rows the purge removed, counted server-side before the delete ran. */
export interface PurgeWorkflowsResult {
  ok: true;
  workflows: number;
  steps: number;
  /** Subset of `steps` that was sitting in someone's approval queue. */
  approvals: number;
  trackedEmails: number;
  auditEntries: number;
}

/**
 * Deletes every workflow and its cascaded children.
 *
 * @returns counts of what was removed
 * @throws ApiClientError(403) when the caller isn't an admin, or when the
 *   deploy is production (blocked there for every role)
 * @sideEffects HTTP POST; hard-deletes all Workflow rows and cascades
 *   StepExecutions, TrackedEmails, and AuditEntries. No external system is
 *   touched — monday rows, Drive files, and sent mail are unaffected.
 * @idempotent yes — a second call removes nothing and returns zeros.
 */
export async function purgeWorkflows(): Promise<PurgeWorkflowsResult> {
  return api<PurgeWorkflowsResult>('/api/admin/purge-workflows', {
    method: 'POST',
    body: JSON.stringify({ confirm: true }),
  });
}
